import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LOOKUP_PAGE_SIZE,
} from '../constants';
import type {
  ImportRecord,
  ImportTotalsParams,
  PagedResult,
} from '../types/index';
import { invalidateListRequest } from '../utils/requestVersion';
import { getNextSortCriterion, type FieldSortCriterionValue } from '../utils/tableSort';

const IMPORT_HISTORY_PAGE_SIZE = 10;
const IMPORT_HISTORY_DEFAULT_SORT: FieldSortCriterionValue = {
  fieldName: 'importedAt',
  isAscending: false,
};
const IMPORT_HISTORY_DESCENDING_FIRST_FIELDS = new Set(['importedAt']);

export type UseImportsHistoryParams = {
  enabled?: boolean;
  organizationId?: number | string | null;
};

export type UseImportsHistoryResult = {
  changePage: (offset: number) => void;
  changePageSize: (pageSize: number) => void;
  error: unknown;
  isLoading: boolean;
  results: PagedResult<ImportRecord>;
  sort: FieldSortCriterionValue;
  toggleSort: (fieldName: string) => void;
};

type ImportsHistoryState = {
  data: ImportRecord[];
  error: unknown;
  isLoading: boolean;
  requestKey: string;
  total: number;
};

function normalizeImports(imports: ImportRecord[] = []): ImportRecord[] {
  return (imports || []).map((item) => ({
    ...item,
    id: item?.id,
    importedAt: item?.importedAt || null,
  }));
}

export async function getImports(
  organizationId: number | string,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE
): Promise<ImportRecord[]> {
  const response = await axios.get('/import', {
    params: { organizationId, page, pageSize },
  });
  return normalizeImports(Array.isArray(response.data) ? response.data : []);
}

export async function getImportsTotals({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
  sortField = null,
  sortAscending = null,
}: ImportTotalsParams = {}, signal?: AbortSignal): Promise<PagedResult<ImportRecord>> {
  const response = await axios.get('/import/totals', {
    params: { organizationId, page, pageSize, sortField, sortAscending },
    ...(signal ? { signal } : {}),
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: normalizeImports(Array.isArray(response.data?.data) ? response.data.data : []),
  };
}

export function useImportsHistory({
  enabled = true,
  organizationId = null,
}: UseImportsHistoryParams = {}): UseImportsHistoryResult {
  const [page, setPage] = useState(DEFAULT_LIST_PAGE);
  const [pageSize, setPageSize] = useState(IMPORT_HISTORY_PAGE_SIZE);
  const [sort, setSort] = useState<FieldSortCriterionValue>(IMPORT_HISTORY_DEFAULT_SORT);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const requestKey = useMemo(
    () => `${enabled}:${String(organizationId ?? '')}:${page}:${pageSize}:${sort.fieldName}:${sort.isAscending}`,
    [enabled, organizationId, page, pageSize, sort.fieldName, sort.isAscending],
  );
  const [state, setState] = useState<ImportsHistoryState>({
    data: [],
    error: null,
    isLoading: Boolean(enabled && organizationId),
    requestKey: '',
    total: 0,
  });
  const [paginationOrganizationId, setPaginationOrganizationId] = useState(organizationId);

  if (paginationOrganizationId !== organizationId) {
    setPaginationOrganizationId(organizationId);
    setPage(DEFAULT_LIST_PAGE);
    setState({
      data: [],
      error: null,
      isLoading: Boolean(enabled && organizationId),
      requestKey: '',
      total: 0,
    });
  }

  useEffect(() => {
    invalidateListRequest(requestIdRef, () => abortControllerRef.current?.abort());
    setPage(DEFAULT_LIST_PAGE);
  }, [organizationId]);

  const loadHistory = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    abortControllerRef.current?.abort();

    if (!enabled || !organizationId) {
      abortControllerRef.current = null;
      setState({ data: [], error: null, isLoading: false, requestKey, total: 0 });
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setState((current) => ({
      ...current,
      data: [],
      error: null,
      isLoading: true,
      requestKey,
    }));

    try {
      const response = await getImportsTotals({
        organizationId,
        page,
        pageSize,
        sortField: sort.fieldName,
        sortAscending: sort.isAscending,
      }, abortController.signal);
      if (requestId !== requestIdRef.current) return;

      setState({
        data: response.data,
        error: null,
        isLoading: false,
        requestKey,
        total: response.total,
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setState({ data: [], error, isLoading: false, requestKey, total: 0 });
    }
  }, [enabled, organizationId, page, pageSize, requestKey, sort.fieldName, sort.isAscending]);

  useEffect(() => {
    void loadHistory();
    return () => {
      invalidateListRequest(requestIdRef, () => abortControllerRef.current?.abort());
    };
  }, [loadHistory]);

  const toggleSort = useCallback((fieldName: string) => {
    invalidateListRequest(requestIdRef, () => abortControllerRef.current?.abort());
    setState((current) => ({ ...current, data: [], error: null, isLoading: true }));
    setPage(DEFAULT_LIST_PAGE);
    setSort((current) => getNextSortCriterion(
      current,
      fieldName,
      IMPORT_HISTORY_DESCENDING_FIRST_FIELDS,
    ));
  }, []);

  const changePage = useCallback((offset: number) => {
    invalidateListRequest(requestIdRef, () => abortControllerRef.current?.abort());
    setState((current) => ({ ...current, data: [], error: null, isLoading: true }));
    setPage((current) => current + offset);
  }, []);

  const changePageSize = useCallback((nextPageSize: number) => {
    invalidateListRequest(requestIdRef, () => abortControllerRef.current?.abort());
    setState((current) => ({ ...current, data: [], error: null, isLoading: true }));
    setPage(DEFAULT_LIST_PAGE);
    setPageSize(nextPageSize);
  }, []);

  const isCurrentRequest = state.requestKey === requestKey;
  const shouldLoad = Boolean(enabled && organizationId);

  return {
    changePage,
    changePageSize,
    error: isCurrentRequest ? state.error : null,
    isLoading: shouldLoad && (!isCurrentRequest || state.isLoading),
    results: {
      page,
      pageSize,
      total: state.total,
      data: isCurrentRequest ? state.data : [],
    },
    sort,
    toggleSort,
  };
}
